/*
Organization Functions Service

This service handles all API calls for organization functions.
Following established patterns from PROJECT_CONVENTIONS.md:
- Uses axios with withCredentials for session authentication
- Standardized response format conversion
- Error handling with proper status codes
- Base URL configuration

TODO: Phase 2 Implementation Tasks:
1. Implement all CRUD operations with proper error handling
2. Add request/response type validation
3. Integrate with authentication system and handle 401s
4. Add retry logic for failed requests
5. Implement caching for frequently accessed data
6. Add request cancellation for component unmounting
7. Add loading states and progress indicators
*/

import apiClient from './apiService';
import { AxiosResponse } from 'axios';

// Use shared apiClient instead of custom instance
const apiService = apiClient;

// Types (TODO: Import from shared types)
export interface OrganizationFunction {
  id: number;
  entity_id: number;
  name: string;
  description?: string;
  parent_function_id?: number;
  code?: string;
  code_seq?: number;
  is_active: number;
  is_approved: number;
  created_by: number;
  created_on: string;
  modified_by?: number;
  modified_on?: string;
  workflow_state?: string;
  submitted_by?: number;
  submitted_on?: string;
  approved_by?: number;
  approved_on?: string;
  approval_note?: string;
  rejected_by?: number;
  rejected_on?: string;
  rejection_reason?: string;
}

export interface FunctionQueryParams {
  status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
  search?: string;
  tree?: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  count?: number;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface FunctionStatistics {
  total_functions: number;
  active_functions: number;
  inactive_functions: number;
  hierarchy_levels: number;
}

class OrganizationFunctionsService {

  /**
   * Get all organization functions with optional filtering
   */
  async getAll(params?: FunctionQueryParams): Promise<OrganizationFunction[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.status) {
        queryParams.append('status', params.status);
      }
      if (params?.search) {
        queryParams.append('search', params.search);
      }
      if (params?.tree) {
        queryParams.append('tree', 'true');
      }

      const queryString = queryParams.toString();
      const url = `/api/v1/org/functions${queryString ? `?${queryString}` : ''}`;

      const response: AxiosResponse<any> =
        await apiService.get(url);

      if (response.data && response.data.data) {
        return response.data.data;
      } else {
        throw new Error('Failed to fetch functions');
      }

    } catch (error: any) {
      console.error('Error fetching functions:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch organization functions');
    }
  }

  /**
   * Get a paginated page of functions. Backend returns { data, meta: { total, page, limit, pages } }.
   */
  async getPage(params: {
    page: number;
    limit: number;
    status?: 'ALL' | 'ACTIVE' | 'INACTIVE';
    search?: string;
  }): Promise<{ items: OrganizationFunction[]; total: number }> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', String(params.page));
      queryParams.append('limit', String(params.limit));
      if (params.status) {
        queryParams.append('status', params.status);
      }
      if (params.search) {
        queryParams.append('search', params.search);
      }

      const response: AxiosResponse<any> =
        await apiService.get(`/api/v1/org/functions?${queryParams.toString()}`);

      return {
        items: response.data?.data || [],
        total: response.data?.meta?.total ?? 0,
      };
    } catch (error: any) {
      console.error('Error fetching functions page:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch functions');
    }
  }

  /**
   * Approved + active departments only, for the "Falls Under" parent picker.
   * Hits the dedicated /dropdown endpoint (workflow_state = 'approved'), so
   * drafts / pending / rejected departments never leak into the picker, and
   * it is not subject to the table's pagination.
   */
  async getParentOptions(): Promise<Array<{ id: number; name: string }>> {
    try {
      const response: AxiosResponse<any> =
        await apiService.get('/api/v1/org/functions/dropdown');
      // get_dropdown returns { total, eligible, items: [{ id, name }] }
      const items = response.data?.items ?? response.data?.data ?? response.data ?? [];
      return Array.isArray(items) ? items : [];
    } catch (error: any) {
      console.error('Error fetching department parent options:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch department options');
    }
  }

  /**
   * Get a specific function by ID
   */
  async getById(functionId: number): Promise<OrganizationFunction> {
    try {
      const response: AxiosResponse<any> =
        await apiService.get(`/api/v1/org/functions/${functionId}`);

      const result = response.data?.data || response.data;
      if (result) {
        return result;
      } else {
        throw new Error('Function not found');
      }

    } catch (error: any) {
      console.error(`Error fetching function ${functionId}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to fetch function');
    }
  }

  /**
   * Create a new organization function
   */
  async create(functionData: Partial<OrganizationFunction>): Promise<OrganizationFunction> {
    try {
      const response: AxiosResponse<any> =
        await apiService.post('/api/v1/org/functions', functionData);

      // Backend may return {success, data} or the object directly
      const result = response.data?.data || response.data;
      if (result) {
        return result;
      } else {
        throw new Error('Failed to create function');
      }

    } catch (error: any) {
      console.error('Error creating function:', error);
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to create function');
    }
  }

  /**
   * Update an existing organization function
   */
  async update(functionId: number, updateData: Partial<OrganizationFunction>): Promise<OrganizationFunction> {
    try {
      const response: AxiosResponse<any> =
        await apiService.put(`/api/v1/org/functions/${functionId}`, updateData);

      const result = response.data?.data || response.data;
      if (result) {
        return result;
      } else {
        throw new Error('Failed to update function');
      }

    } catch (error: any) {
      console.error(`Error updating function ${functionId}:`, error);
      throw new Error(error.response?.data?.detail || error.response?.data?.error || 'Failed to update function');
    }
  }

  /**
   * Delete an organization function
   */
  async delete(functionId: number): Promise<void> {
    try {
      const response: AxiosResponse<void> =
        await apiService.delete(`/api/v1/org/functions/${functionId}`);

      if (response.status !== 204 && response.status !== 200) {
        throw new Error('Failed to delete function');
      }

    } catch (error: any) {
      console.error(`Error deleting function ${functionId}:`, error);
      throw new Error(error.response?.data?.error || 'Failed to delete function');
    }
  }

  /**
   * Get functions in hierarchical tree structure
   */
  async getTree(): Promise<OrganizationFunction[]> {
    try {
      // For now, we'll fetch all and build the tree if needed
      return await this.getAll({ tree: true });
    } catch (error: any) {
      console.error('Error fetching function tree:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch function tree');
    }
  }

  /**
   * Get function statistics
   */
  async getStatistics(): Promise<FunctionStatistics> {
    try {
      const response: AxiosResponse<FunctionStatistics> =
        await apiService.get('/api/v1/org/functions/stats');

      if (response.data) {
        return response.data;
      } else {
        throw new Error('Failed to fetch statistics');
      }

    } catch (error: any) {
      console.error('Error fetching function statistics:', error);
      throw new Error(error.response?.data?.error || 'Failed to fetch function statistics');
    }
  }

  /**
   * Validate function data without creating/updating
   * TODO: Implement client-side and server-side validation
   */
  async validateData(functionData: Partial<OrganizationFunction>, isUpdate: boolean = false): Promise<ValidationResult> {
    try {
      // TODO: Add client-side validation first
      const clientErrors = this.validateClientSide(functionData, isUpdate);
      if (clientErrors.length > 0) {
        return { valid: false, errors: clientErrors };
      }

      // TODO: Make API call for server-side validation
      const queryParam = isUpdate ? '?update=true' : '';
      const response: AxiosResponse<ValidationResult> =
        await apiService.post(`/organization/functions/validate${queryParam}`, functionData);

      return response.data;

    } catch (error: any) {
      console.error('Error validating function data:', error);
      return {
        valid: false,
        errors: ['Validation service error']
      };
    }
  }

  /**
   * Client-side validation helper
   */
  private validateClientSide(data: Partial<OrganizationFunction>, isUpdate: boolean): string[] {
    const errors: string[] = [];

    if (!isUpdate) {
      if (!data.name) {
        errors.push('Function name is required');
      }
    }

    return errors;
  }

  /**
   * Check service health
   * TODO: Implement health check for monitoring
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    try {
      // TODO: Make API call to health endpoint
      const response: AxiosResponse<{ module: string; status: string; timestamp: string }> =
        await apiService.get('/organization/functions/health');

      return {
        status: response.data.status,
        timestamp: response.data.timestamp
      };

    } catch (error: any) {
      console.error('Function service health check failed:', error);
      return {
        status: 'unhealthy',
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Cancel ongoing requests (for component cleanup)
   * TODO: Implement request cancellation
   */
  cancelRequests(): void {
    // TODO: Implement AbortController for request cancellation
    console.log('Request cancellation not yet implemented');
  }
}

// Export singleton instance
const organizationFunctionsService = new OrganizationFunctionsService();
export default organizationFunctionsService;

export const submitFunction = (id: number): Promise<void> =>
  apiClient.post(`/api/v1/org/functions/${id}/submit`).then(() => undefined);

export const approveFunction = (id: number, note?: string): Promise<void> =>
  apiClient.post(`/api/v1/org/functions/${id}/approve`, { note }).then(() => undefined);

export const rejectFunction = (id: number, reason: string): Promise<void> =>
  apiClient.post(`/api/v1/org/functions/${id}/reject`, { reason }).then(() => undefined);

export const reopenFunction = (id: number): Promise<void> =>
  apiClient.post(`/api/v1/org/functions/${id}/reopen`).then(() => undefined);

export const getFunctionHistory = async (
  id: number
): Promise<import('../components/common/AuditHistoryModal').AuditRecord[]> => {
  const response = await apiClient.get<{ items: any[] }>(`/api/v1/org/functions/${id}/history`);
  const rows = response.data.items ?? (response.data as any) ?? [];
  return rows.map((r: any) => ({
    audit_id: r.id,
    entity_id: r.entity_id,
    action: r.event_type === 'state_transition' ? 'STATE' : 'EDIT',
    changed_by: r.changed_by,
    changed_by_username: r.changed_by_username || 'Unknown',
    changed_on: r.changed_on,
    change_reason: r.remarks,
  }));
};